import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "../Grid.module.scss";
import { useLayoutPropsUtil } from "../../../internal/hooks";
import { LayoutUtilProps } from "../../../types";

/**
 * Props for the GridItem component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type GridItemProps = ComponentPropsWithoutRef<"div"> & LayoutUtilProps;

/**
 * GridItem component for individual items within a CSS Grid layout.
 *
 * Features:
 * - Individual grid item with full layout utility support
 * - Automatic grid item styling and class management
 * - Comprehensive layout utilities for positioning and sizing
 * - Support for all standard div element props
 * - Flexible grid positioning and sizing controls
 * - Automatic style generation and class management
 * - Works seamlessly with Grid component
 *
 * @example
 * <Grid templateColumns="repeat(2, 1fr)" gap="4">
 *   <Grid.Item>Grid Item 1</Grid.Item>
 *   <Grid.Item>Grid Item 2</Grid.Item>
 * </Grid>
 */
export const GridItem = forwardRef<HTMLDivElement, GridItemProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const { className, children, style, ...rest } = componentProps;

    const gridItemClassNames = cx([styles["grid-item"]], className, {});

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <div
        className={gridItemClassNames}
        data-anv="grid-item"
        ref={ref}
        style={styleCombined}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

GridItem.displayName = "GridItem";
